import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { RankingItem as ApiRankingItem } from '../api/ranking'
import { getShortAddress } from '../utils/address'
import { formatScoreOneDecimal } from '../utils/scoreFormat'
import Gap from './Gap'

interface RankingItemProps {
  ranking: ApiRankingItem
  isLast?: boolean
  onClick?: (ranking: ApiRankingItem) => void
  isEditMode?: boolean
  displayRank?: number // 표시할 순위 (없으면 ranking.rank 사용)
  hideScore?: boolean // true면 점수 대신 ? 점 표시 (카테고리별 5개 미만일 때)
}

const RankingItem: React.FC<RankingItemProps> = ({
  ranking,
  onClick,
  isEditMode = false,
  displayRank,
  hideScore = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ranking.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  const handleClick = () => {
    if (isEditMode) {
      return
    }
    if (onClick) {
      onClick(ranking)
    }
  }

  // 점수 표시 (rating 사용)
  const displayScore = ranking.rating

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full min-w-0 flex items-center py-16px px-20px ${isEditMode ? '' : 'active:bg-brandPrimary-10%'} ${isDragging ? 'cursor-grabbing' : isEditMode ? 'cursor-default' : 'cursor-pointer'}`}
      onClick={handleClick}
    >
      {/* 햄버거 버튼 (수정 모드일 때만 표시, 드래그 터치 영역은 여기만) */}
      {isEditMode && (
        <>
          <div
            {...attributes}
            {...listeners}
            className="flex items-center justify-center min-w-44px min-h-44px py-10px pl-4px pr-12px cursor-grab active:cursor-grabbing touch-none"
          >
            <img
              src="/images/hamburger-icon.png"
              alt="드래그"
              className="w-24px h-24px object-contain pointer-events-none"
            />
          </div>
          <Gap width={8} />
        </>
      )}

      {/* 순위 번호 (큰 숫자) — 자리수(1~3자리) 무관 고정폭 + 숫자 가운데정렬
          → 가게명 시작 위치는 항상 일정, 숫자는 칸 가운데 */}
      <div className="flex shrink-0 items-center justify-center py-8px min-w-44px">
        <span className="font-pretendard font-bold text-24px leading-none text-brown-80%-main text-center">
          {displayRank ?? ranking.rank}
        </span>
      </div>

      <Gap width={8} />

      {/* 중앙 정보 */}
      <div className="min-w-0 flex-1 flex flex-col items-start">
        <div className="w-full break-words font-pretendard font-bold text-16px leading-24px text-brown-80%-main text-left">
          {ranking.name}
        </div>
        <div className="w-full break-words font-pretendard font-regular text-16px leading-20px text-textColorText2 text-left">
          {getShortAddress(ranking.address)}
        </div>
      </div>

      <Gap width={8} />

      {/* 오른쪽 정보 —
          점수·세부분류에만 오른쪽 여백 8px를 더 준다.
          행 여백(20px)에 딱 붙으면 16px 글자가 화면 가장자리에 너무 가까워
          답답해 보인다. 순위 숫자는 24px로 큼직해 붙어도 괜찮지만 이쪽은 다르다.
          그래서 좌우가 20px / 28px로 비대칭이 되는 건 의도한 것이다. */}
      <div className="flex shrink-0 flex-col items-end pr-8px">
        <div className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main text-right">
          {hideScore ? '? 점' : `${formatScoreOneDecimal(displayScore)} 점`}
        </div>
        {ranking.subCategory && (
          <div className="font-pretendard font-regular text-16px leading-20px text-textColorText2 text-right">
            {ranking.subCategory}
          </div>
        )}
      </div>
    </div>
  )
}

export default RankingItem
